import { useRef } from 'react';
import { Button, Card } from '@orion/ui';
import type { AttachmentTarget } from '@orion/contracts';
import { useAttachmentsQuery, useDeleteAttachment, useUploadAttachment } from './api.js';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** docs/architecture/08-v1-scope.md § Leads "attachments" — files live on
 * disk behind FileStoragePort (modules/storage), never in Postgres; this
 * panel just lists/uploads/deletes/downloads for one parent record. */
export function AttachmentsPanel(props: AttachmentTarget) {
  const { data, isLoading } = useAttachmentsQuery(props);
  const upload = useUploadAttachment(props);
  const remove = useDeleteAttachment(props);
  const fileInput = useRef<HTMLInputElement>(null);

  const attachments = data ?? [];

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await upload.mutateAsync(file);
    if (fileInput.current) fileInput.current.value = '';
  };

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">Attachments</h3>
        <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()} loading={upload.isPending}>
          Upload
        </Button>
        <input ref={fileInput} type="file" className="hidden" onChange={onFileChosen} />
      </div>

      {!isLoading && attachments.length === 0 && <p className="text-sm text-text-muted">No files yet.</p>}

      <div className="flex flex-col gap-2">
        {attachments.map((a) => (
          <div key={a.id} className="flex items-center justify-between rounded-control border border-border px-3 py-2 text-sm">
            <a href={`/api/v1/attachments/${a.id}/download`} className="min-w-0 flex-1 truncate font-medium text-primary hover:underline">
              {a.fileName}
            </a>
            <span className="ml-3 shrink-0 text-xs text-text-muted">{formatSize(a.sizeBytes)}</span>
            <button
              type="button"
              onClick={() => remove.mutate(a.id)}
              className="ml-3 shrink-0 text-xs font-semibold text-danger hover:underline"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}
